export type CompanyAddressType = 'BILLING' | 'COMPANY' | 'CONTROL' | string;

export interface CompanyAddressLike {
  addressLine1?: string | null;
  addressLine2?: string | null;
  city?: string | null;
  state?: string | null;
  postalCode?: string | null;
  country?: string | null;
  type?: CompanyAddressType | null;
  identityNumber?: string | null;
  phone?: string | null;
  taxNumber?: string | null;
  companyName?: string | null;
  taxOffice?: string | null;
  title?: string | null;
  fullName?: string | null;
  sgkNumber?: string | null;
  latitude?: number | null;
  longitude?: number | null;
}

export interface CompanyInfoInput {
  name?: string | null;
  taxOffice?: string | null;
  taxNumber?: string | null;
  email?: string | null;
  phoneNumber?: string | null;
  address?: string | null;
  city?: string | null;
  district?: string | null;
}

export interface BillingInfoInput {
  invoiceIssuedToDifferentOrganization?: boolean | null;
  companyName?: string | null;
  email?: string | null;
  phoneNumber?: string | null;
  address?: string | null;
  city?: string | null;
  district?: string | null;
}

export interface CompanyBillingPayloadInput {
  company: CompanyInfoInput;
  billing?: BillingInfoInput | null;
  existingCompanyAddress?: CompanyAddressLike | null;
  existingBillingAddress?: CompanyAddressLike | null;
}

const clean = (value?: string | null) => String(value ?? '').trim();
const lower = (value?: string | null) => clean(value).toLowerCase();

export const findAddressByType = (
  addresses: CompanyAddressLike[] | undefined | null,
  type: CompanyAddressType
) => {
  if (!Array.isArray(addresses)) {
    return null;
  }
  return addresses.find((address) => address?.type === type) ?? null;
};

export const findCompanyAddress = (addresses: CompanyAddressLike[] | undefined | null) => (
  findAddressByType(addresses, 'COMPANY') ?? findAddressByType(addresses, 'BILLING')
);

export const findBillingAddress = (addresses: CompanyAddressLike[] | undefined | null) => (
  findAddressByType(addresses, 'BILLING')
);

export const buildCompanyBillingPayload = ({
  company,
  billing,
  existingCompanyAddress,
  existingBillingAddress,
}: CompanyBillingPayloadInput) => {
  const useDifferentBilling = billing?.invoiceIssuedToDifferentOrganization === true;
  const billingCompanyName = useDifferentBilling ? billing?.companyName : company.name;
  const billingPhoneNumber = useDifferentBilling ? billing?.phoneNumber : company.phoneNumber;
  const billingEmail = useDifferentBilling ? billing?.email : company.email;
  const billingAddressLine = useDifferentBilling ? billing?.address : company.address;
  const billingCity = useDifferentBilling ? billing?.city : company.city;
  const billingDistrict = useDifferentBilling ? billing?.district : company.district;

  const companyAddress: CompanyAddressLike = {
    addressLine1: clean(company.address),
    addressLine2: '',
    city: clean(company.city),
    state: clean(company.district),
    postalCode: existingCompanyAddress?.postalCode ?? '',
    country: existingCompanyAddress?.country ?? 'Turkey',
    type: 'COMPANY',
    identityNumber: existingCompanyAddress?.identityNumber ?? '',
    phone: clean(company.phoneNumber),
    taxNumber: clean(company.taxNumber),
    companyName: clean(company.name),
    taxOffice: clean(company.taxOffice),
    title: existingCompanyAddress?.title || 'Firma Adresi',
    fullName: existingCompanyAddress?.fullName ?? '',
    sgkNumber: existingCompanyAddress?.sgkNumber ?? '',
    latitude: existingCompanyAddress?.latitude ?? null,
    longitude: existingCompanyAddress?.longitude ?? null,
  };

  const billingAddressPayload: CompanyAddressLike = {
    addressLine1: clean(billingAddressLine),
    addressLine2: '',
    city: clean(billingCity),
    state: clean(billingDistrict),
    postalCode: existingBillingAddress?.postalCode ?? '',
    country: existingBillingAddress?.country ?? 'Turkey',
    type: 'BILLING',
    identityNumber: existingBillingAddress?.identityNumber ?? '',
    phone: clean(billingPhoneNumber),
    taxNumber: clean(company.taxNumber),
    companyName: clean(billingCompanyName),
    taxOffice: clean(company.taxOffice),
    title: existingBillingAddress?.title || 'Fatura Adresi',
    fullName: existingBillingAddress?.fullName ?? '',
    sgkNumber: existingBillingAddress?.sgkNumber ?? '',
    latitude: existingBillingAddress?.latitude ?? null,
    longitude: existingBillingAddress?.longitude ?? null,
  };

  return {
    invoiceIssuedToDifferentOrganization: useDifferentBilling,
    billingEmail: lower(billingEmail),
    companyAddress,
    billingAddress: billingAddressPayload,
  };
};
